import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  MEMBER_STATUS, ROLES, longDate, money, shortDate,
  type Invoice, type Member, type MemberStatus, type Role,
} from '@lcc/shared';
import { api, openAuthedFile } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Card, Empty, ErrorNote, Loading, SelectField, Status, TextField,
  errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';

interface SignatureRow {
  signatureId: string; title: string; version: string; state: string;
  signedAt: string | null; typedName: string | null; signedByName: string | null;
  isGuardian: boolean; verificationCode: string | null; overrideReason: string | null;
  hasDocument: boolean; ipAddress: string | null;
}

interface Clearance {
  data: {
    isCleared: boolean; requiredCount: number; clearedCount: number;
    outstandingCount: number;
    outstanding: Array<{ requirementId: string; code: string; title: string; state: string }>;
  };
}

export function MemberDetailPage() {
  const { id = '' } = useParams();
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();

  const member = useQuery({
    queryKey: ['member', id],
    queryFn: () => api.get<{ data: Member }>(`/members/${id}`).then((r) => r.data),
  });

  const clearance = useQuery({
    queryKey: ['clearance', id],
    queryFn: () => api.get<Clearance>(`/waivers/clearance/${id}`).then((r) => r.data),
  });

  const signatures = useQuery({
    queryKey: ['member', id, 'signatures'],
    enabled: Boolean(id),
    queryFn: () => api
      .get<{ data: SignatureRow[] }>(`/waivers/signatures?memberId=${id}`)
      .then((r) => r.data),
  });
  const cleared = clearance.data;

  const invoices = useQuery({
    queryKey: ['invoices', 'member', id],
    queryFn: () => api.get<{ data: Invoice[] }>(`/money/invoices?memberId=${id}`).then((r) => r.data),
    enabled: isAdmin,
  });

  const [edit, setEdit] = useState<Partial<Member>>({});
  const dirty = Object.keys(edit).length > 0;

  const save = useMutation({
    mutationFn: () => api.patch<{ data: Member }>(`/members/${id}`, edit),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['member', id] });
      void qc.invalidateQueries({ queryKey: ['members'] });
      setEdit({});
      toast.success('Saved.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const setRoles = useMutation({
    mutationFn: (roles: Role[]) => api.patch(`/members/${id}/roles`, { roles }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['member', id] });
      toast.success('Roles updated.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const createLogin = useMutation({
    mutationFn: () => api.post<{ emailed: string }>(`/members/${id}/login`),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['member', id] });
      toast.success(`Login created — temporary password emailed to ${r.emailed}.`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (member.isLoading) return <><PageHeader title="Member" /><div className="content"><Loading /></div></>;
  if (member.error) return <><PageHeader title="Member" /><div className="content"><ErrorNote error={member.error} /></div></>;

  const m = member.data!;
  const value = <K extends keyof Member>(k: K): Member[K] => (k in edit ? edit[k]! : m[k]) as Member[K];
  const set = <K extends keyof Member>(k: K) => (v: Member[K]) => setEdit((e) => ({ ...e, [k]: v }));
  const hasLogin = m.roles.length > 0;

  return (
    <>
      <PageHeader
        title={m.fullName}
        sub={<><Link to="/members">Members</Link> · joined {m.joinedOn ? longDate(m.joinedOn) : 'unknown'}</>}
        actions={dirty && (
          <>
            <button className="btn" type="button" onClick={() => setEdit({})}>Discard</button>
            <button className="btn btn--accent" type="button"
              disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </>
        )}
      />

      <div className="content stack">
        {cleared && !cleared.isCleared && (
          <div className="banner banner--warn">
            <strong>
              {cleared.outstandingCount} waiver
              {cleared.outstandingCount === 1 ? '' : 's'} outstanding.
            </strong>{' '}
            This member cannot be added to a team until they are cleared:{' '}
            {cleared.outstanding.map((o) => o.title).join(', ')}.{' '}
            <Link to="/waivers">Manage waivers &rarr;</Link>
          </div>
        )}

        <div className="grid grid--2">
          <Card title="Details">
            <fieldset disabled={save.isPending}>
              <div className="grid grid--2">
                <TextField label="First name" value={value('firstName')} onChange={set('firstName')} />
                <TextField label="Last name" value={value('lastName')} onChange={set('lastName')} />
              </div>
              <TextField label="Email" type="email" value={value('email') ?? ''}
                onChange={(v) => set('email')(v || null)} />
              <div className="grid grid--2">
                <TextField label="Phone" value={value('phone') ?? ''}
                  onChange={(v) => set('phone')(v || null)} />
                <SelectField label="Status" value={value('status')}
                  onChange={set('status') as (v: MemberStatus) => void}
                  options={MEMBER_STATUS.map((s) => ({ value: s, label: s }))} />
              </div>
              <TextField label="Address" value={value('address') ?? ''}
                onChange={(v) => set('address')(v || null)} />
              <div className="grid grid--2">
                <TextField label="City" value={value('city') ?? ''}
                  onChange={(v) => set('city')(v || null)} />
                <TextField label="Postal code" value={value('postalCode') ?? ''}
                  onChange={(v) => set('postalCode')(v || null)} />
              </div>
            </fieldset>
          </Card>

          <div className="stack">
            <Card title="Emergency contact">
              <fieldset disabled={save.isPending}>
                <TextField label="Name" value={value('emergencyContactName') ?? ''}
                  onChange={(v) => set('emergencyContactName')(v || null)} />
                <TextField label="Phone" value={value('emergencyContactPhone') ?? ''}
                  onChange={(v) => set('emergencyContactPhone')(v || null)} />
                <TextField label="Accessibility notes" value={value('accessibilityNotes') ?? ''}
                  onChange={(v) => set('accessibilityNotes')(v || null)}
                  hint="Anything the ice crew or conveners should know." />
              </fieldset>
            </Card>

            {isAdmin && (
              <Card title="Portal access">
                {hasLogin ? (
                  <div className="stack stack--tight">
                    <p className="small muted">This member can sign in to the portal and app.</p>
                    <div className="row row--tight">
                      {ROLES.filter((r) => r !== 'member').map((r) => {
                        const on = m.roles.includes(r);
                        return (
                          <button
                            key={r} type="button"
                            className={on ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
                            disabled={setRoles.isPending}
                            onClick={() => setRoles.mutate(
                              on ? m.roles.filter((x) => x !== r) : [...m.roles, r],
                            )}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>
                    <p className="small muted">Click a role to grant or revoke it.</p>
                  </div>
                ) : (
                  <div className="stack stack--tight">
                    <p className="small muted">
                      No login yet. Creating one emails a temporary password.
                    </p>
                    <button className="btn btn--primary" type="button"
                      disabled={!m.email || createLogin.isPending}
                      onClick={() => createLogin.mutate()}>
                      {createLogin.isPending ? 'Creating…' : 'Create a login'}
                    </button>
                    {!m.email && <p className="small" style={{ color: 'var(--red-600)' }}>
                      Add an email address first.
                    </p>}
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>

        <Card title="Waivers on file" flush>
          {signatures.isLoading ? <Loading />
            : !signatures.data?.length
              ? <Empty title="Nothing signed yet">
                  This member has no waiver signature on file.
                </Empty>
              : (
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr><th>Waiver</th><th>Version</th><th>Signed</th>
                        <th>Signed by</th><th>Code</th><th>Copy</th></tr>
                    </thead>
                    <tbody>
                      {signatures.data.map((sg) => (
                        <tr key={sg.signatureId}>
                          <td>{sg.title}</td>
                          <td className="mono">{sg.version}</td>
                          <td className="nowrap small">{shortDate(sg.signedAt)}</td>
                          <td className="small">
                            {sg.state === 'waived'
                              ? <em>recorded by the office</em>
                              : (sg.typedName ?? sg.signedByName ?? '—')}
                            {sg.isGuardian && <span className="muted"> · guardian</span>}
                            {sg.overrideReason && (
                              <div className="small muted">{sg.overrideReason}</div>
                            )}
                          </td>
                          <td className="mono small">{sg.verificationCode ?? '—'}</td>
                          <td>
                            {sg.hasDocument
                              ? <SignedCopyButton signatureId={sg.signatureId} />
                              : <span className="small muted">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </Card>

        {isAdmin && (
          <Card title="Invoices" flush>
            {invoices.isLoading ? <Loading />
              : !invoices.data?.length ? <Empty title="No invoices" />
              : (
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr><th>#</th><th>Issued</th><th>Notes</th>
                        <th className="num">Total</th><th className="num">Balance</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {invoices.data.map((inv) => (
                        <tr key={inv.id}>
                          <td className="mono">{inv.number}</td>
                          <td className="nowrap small">{shortDate(inv.issuedOn)}</td>
                          <td className="small muted">{inv.notes ?? '—'}</td>
                          <td className="num">{money(inv.totalCents)}</td>
                          <td className="num">{money(inv.balanceCents)}</td>
                          <td><Status value={inv.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        )}
      </div>
    </>
  );
}

/** A signed waiver PDF is behind the bearer token, so it cannot be a plain link. */
function SignedCopyButton({ signatureId }: { signatureId: string }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <button
      className="btn btn--sm"
      type="button"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        openAuthedFile(`/waivers/signatures/${signatureId}/document`)
          .catch((e) => toast.error(errorMessage(e)))
          .finally(() => setBusy(false));
      }}
    >
      {busy ? 'Opening…' : 'Open PDF'}
    </button>
  );
}
